'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { FaMicrophone, FaStop } from 'react-icons/fa';
import useAudioRecorder from '@/hooks/useAudioRecorder';
import DigitalHumanStore from '@/stores/digital-human-store';
import styles from './MicButton.module.css';
import logger from '@/lib/logger';

interface MicButtonProps {
  store: DigitalHumanStore;
  connectionStatus: 'connecting' | 'connected' | 'error';
  addUserAudio: (base64Audio: string) => void;
  cancelAssistantResponse: () => void;
  triggerSpokenMessage: (text: string) => void;
  welcomeMessage: string;
}

export const MicButton = observer(({
  store,
  connectionStatus,
  addUserAudio,
  cancelAssistantResponse,
  triggerSpokenMessage,
  welcomeMessage
}: MicButtonProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const recorderStartedRef = useRef(false);
  const hasTriggeredInitialGreetingRef = useRef(false);
  const bargeInConsecutiveRef = useRef(0);
  const bargeInTriggeredRef = useRef(false);
  const bargeInConfig = useMemo(() => {
    const profileRaw = (process.env.NEXT_PUBLIC_BARGE_IN_PROFILE ?? 'quiet_room')
      .toLowerCase()
      .trim();

    const profileDefaults =
      profileRaw === 'noisy_showroom'
        ? {
            thresholdDbfs: -31,
            minConsecutiveChunks: 6,
            startupGuardMs: 500
          }
        : {
            // Default profile: quiet room (more sensitive).
            thresholdDbfs: -39,
            minConsecutiveChunks: 4,
            startupGuardMs: 300
          };

    const thresholdOverride = Number(process.env.NEXT_PUBLIC_BARGE_IN_THRESHOLD_DBFS);
    const consecutiveOverride = Number(process.env.NEXT_PUBLIC_BARGE_IN_MIN_CONSECUTIVE_CHUNKS);
    const startupGuardOverride = Number(process.env.NEXT_PUBLIC_BARGE_IN_STARTUP_GUARD_MS);

    return {
      profile: profileRaw === 'noisy_showroom' ? 'noisy_showroom' : 'quiet_room',
      thresholdDbfs: Number.isFinite(thresholdOverride)
        ? thresholdOverride
        : profileDefaults.thresholdDbfs,
      minConsecutiveChunks:
        Number.isFinite(consecutiveOverride) && consecutiveOverride > 0
          ? Math.floor(consecutiveOverride)
          : profileDefaults.minConsecutiveChunks,
      startupGuardMs:
        Number.isFinite(startupGuardOverride) && startupGuardOverride >= 0
          ? Math.floor(startupGuardOverride)
          : profileDefaults.startupGuardMs
    };
  }, []);

  const { start: startRecording, stop: stopRecording, warmup: warmupRecorder } = useAudioRecorder({
    sampleRate: 24000,
    onAudioRecorded: addUserAudio,
    onChunkAnalyzed: ({ rmsDbfs, peakDbfs, isSilent }) => {
      if (!isRecording) {
        bargeInConsecutiveRef.current = 0;
        bargeInTriggeredRef.current = false;
        return;
      }

      if (!store.isAssistantSpeechSuppressed()) {
        bargeInConsecutiveRef.current = 0;
        bargeInTriggeredRef.current = false;
        return;
      }

      const startedAt = store.getAssistantSpeechStartedAtMs();
      const elapsedMs = startedAt > 0 ? Date.now() - startedAt : 0;
      if (elapsedMs < bargeInConfig.startupGuardMs) {
        bargeInConsecutiveRef.current = 0;
        return;
      }

      const isSpeechCandidate = !isSilent && rmsDbfs >= bargeInConfig.thresholdDbfs;
      if (isSpeechCandidate) {
        bargeInConsecutiveRef.current += 1;
      } else {
        bargeInConsecutiveRef.current = 0;
      }

      if (
        !bargeInTriggeredRef.current &&
        bargeInConsecutiveRef.current >= bargeInConfig.minConsecutiveChunks
      ) {
        bargeInTriggeredRef.current = true;
        console.log(
          `🛑 Barge-in triggered | rmsDbfs=${rmsDbfs.toFixed(1)} peakDbfs=${peakDbfs.toFixed(
            1
          )} threshold=${bargeInConfig.thresholdDbfs} consecutive=${bargeInConsecutiveRef.current}`
        );
        cancelAssistantResponse();
        store.releaseMicAfterAssistantSpeech();
        bargeInConsecutiveRef.current = 0;
      }
    },
    requestStream: async () => {
      let stream = store.getGatedMediaStream?.();
      if (!stream && typeof store.createGatedMediaStream === 'function') {
        stream = await store.createGatedMediaStream();

        // Pre-warm the recorder once immediately after the gated stream is created
        try {
          await warmupRecorder();
        } catch (error) {
          console.error('Failed to warm up audio recorder:', error);
        }
      }
      return stream ?? null;
    }
  });

  const isHardwarePttAvailable = store.isPTTServiceConnected();

  useEffect(() => {
    console.log(
      `🎛️ Barge-in profile=${bargeInConfig.profile} | thresholdDbfs=${bargeInConfig.thresholdDbfs}, minConsecutiveChunks=${bargeInConfig.minConsecutiveChunks}, startupGuardMs=${bargeInConfig.startupGuardMs}`
    );
  }, [bargeInConfig]);

  // Start recorder when hardware PTT is available
  useEffect(() => {
    if (isHardwarePttAvailable && !recorderStartedRef.current) {
      console.log('🎤 Hardware PTT detected - starting audio recorder');
      startRecording()
        .then(() => {
          recorderStartedRef.current = true;
          console.log('✅ Audio recorder started for hardware PTT');
        })
        .catch((error) => {
          console.error('❌ Failed to start recorder for hardware PTT:', error);
        });
    }
  }, [isHardwarePttAvailable, startRecording]);

  useEffect(() => {
    return () => {
      if (recorderStartedRef.current) {
        stopRecording();
      }
    };
  }, [stopRecording]);

  const handleClick = useCallback(
    async (event: React.MouseEvent | React.TouchEvent) => {
      event.preventDefault();
      event.stopPropagation();

      try {
        if (isRecording) {
          await store.closeAudioGate?.();
          setIsRecording(false);
          logger.info('🎤 Mic closed (button)');
        } else {
          if (!recorderStartedRef.current) {
            await startRecording();
            recorderStartedRef.current = true;
          }
          await store.openAudioGate?.();
          setIsRecording(true);
          logger.info('🎤 Mic opened (button)');
          if (!hasTriggeredInitialGreetingRef.current && welcomeMessage.trim()) {
            hasTriggeredInitialGreetingRef.current = true;
            triggerSpokenMessage(welcomeMessage);
          }
        }
      } catch (error) {
        console.error('Failed to toggle mic:', error);
      }
    },
    [isRecording, startRecording, store, triggerSpokenMessage, welcomeMessage]
  );

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }, []);

  const getButtonClasses = () => {
    let classes = styles.micButton;
    if (isRecording) {
      classes += ` ${styles.active}`;
    } else {
      classes += ` ${styles.idle}`;
    }
    return classes;
  };

  const isButtonDisabled = connectionStatus !== 'connected';

  if (store.presentationMode) {
    return (
      <div className={styles.container}>
        <span className={styles.presentationNote} title="Microphone disabled so Teams can use it">
          Use Teams for audio
        </span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {!isHardwarePttAvailable && (
      <button
        className={getButtonClasses()}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        disabled={isButtonDisabled}
      >
        {isRecording ? (
          <>
            <FaStop color="#ffffff" size={16} />
            <span className={styles.label}>Stop</span>
          </>
        ) : (
          <>
            <FaMicrophone color="#ffffff" size={16} />
            <span className={styles.label}>Start</span>
          </>
        )}
      </button>
      )}

      {connectionStatus !== 'connected' && (
        <div className={styles.error}>
          {connectionStatus === 'connecting' ? 'Connecting...' : 'Connection failed'}
        </div>
      )}
    </div>
  );
});

